import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const verticalDefaults = {
  home: { label: 'Home Services', missedRate: 35, closeRate: 25, avgProfit: 600 },
  auto: { label: 'Auto Dealerships', missedRate: 28, closeRate: 30, avgProfit: 400 },
  realestate: { label: 'Real Estate Teams', missedRate: 42, closeRate: 12, avgProfit: 8000 },
};

const plans = [
  { label: 'Starter', price: 750 },
  { label: 'Growth', price: 1250 },
  { label: 'Core', price: 2000 },
];

const ROICalculator = () => {
  const [vertical, setVertical] = useState('home');
  const [inputs, setInputs] = useState({ monthlyCalls: 400, missedRate: 35, closeRate: 25, avgProfit: 600 });
  const [plan, setPlan] = useState(750);
  const [results, setResults] = useState({});

  useEffect(() => {
    const d = verticalDefaults[vertical];
    setInputs(prev => ({ ...prev, missedRate: d.missedRate, closeRate: d.closeRate, avgProfit: d.avgProfit }));
  }, [vertical]);

  useEffect(() => {
    const missed = Math.round(inputs.monthlyCalls * (inputs.missedRate / 100));
    const recovered = Math.round(missed * 0.70);
    const converted = Math.round(recovered * (inputs.closeRate / 100));
    const grossProfit = converted * inputs.avgProfit;
    const net = grossProfit - plan;
    const roiMultiple = plan > 0 ? (grossProfit / plan).toFixed(1) : 0;
    setResults({ missed, recovered, converted, grossProfit, net, roiMultiple });
  }, [inputs, plan]);

  const set = (field, val) => setInputs(prev => ({ ...prev, [field]: val }));

  return (
    <section id="roi-calculator" className="py-20 border-t" style={{ background: '#0d0d0d', borderColor: '#1a1a1a' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#4ade80' }}>ROI</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#f1f1f1' }}>Why companies pay for this.</h2>
          <p className="text-base max-w-xl" style={{ color: '#888' }}>Conservative estimates based on your vertical. Adjust to match your actual numbers.</p>
        </motion.div>
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="space-y-6 p-7 rounded-2xl border" style={{ background: '#111', borderColor: '#1f1f1f' }}>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-3" style={{ color: '#666' }}>Vertical</label>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(verticalDefaults).map(([key, v]) => (
                  <button key={key} onClick={() => setVertical(key)}
                    className="px-4 py-2 rounded-lg text-sm font-medium border transition-all"
                    style={{ background: vertical === key ? '#1a3a2a' : '#0d0d0d', borderColor: vertical === key ? '#2d6a4f' : '#1f1f1f', color: vertical === key ? '#4ade80' : '#666' }}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            {[
              { field: 'monthlyCalls', label: 'Monthly inbound calls', min: 100, max: 3000, step: 50, display: inputs.monthlyCalls.toLocaleString() },
              { field: 'missedRate', label: 'Missed-call rate', min: 5, max: 70, step: 5, display: `${inputs.missedRate}%` },
              { field: 'closeRate', label: 'Close rate on recovered calls', min: 5, max: 60, step: 5, display: `${inputs.closeRate}%` },
              { field: 'avgProfit', label: 'Avg gross profit per booked job', min: 100, max: 15000, step: 100, display: `$${inputs.avgProfit.toLocaleString()}` },
            ].map(({ field, label, min, max, step, display }) => (
              <div key={field}>
                <div className="flex justify-between mb-2">
                  <label className="text-sm font-medium" style={{ color: '#aaa' }}>{label}</label>
                  <span className="text-sm font-bold" style={{ color: '#f1f1f1' }}>{display}</span>
                </div>
                <input type="range" min={min} max={max} step={step} value={inputs[field]}
                  onChange={e => set(field, parseInt(e.target.value))}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: '#4ade80', background: '#222' }} />
              </div>
            ))}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-3" style={{ color: '#666' }}>Plan</label>
              <div className="flex gap-2">
                {plans.map(p => (
                  <button key={p.price} onClick={() => setPlan(p.price)}
                    className="flex-1 py-2.5 rounded-lg text-sm font-semibold border transition-all"
                    style={{ background: plan === p.price ? '#1a3a2a' : '#0d0d0d', borderColor: plan === p.price ? '#2d6a4f' : '#1f1f1f', color: plan === p.price ? '#4ade80' : '#666' }}>
                    {p.label}<br />
                    <span className="text-xs font-normal">${p.price}/mo</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-4">
            {[
              { label: 'Missed calls / month', value: results.missed?.toLocaleString(), sub: 'before Valtivo' },
              { label: 'Calls recovered (70% capture)', value: results.recovered?.toLocaleString(), sub: 'answered by AI' },
              { label: 'Estimated booked jobs', value: results.converted?.toLocaleString(), sub: `at ${inputs.closeRate}% close rate` },
              { label: 'Estimated gross profit recovered', value: `$${results.grossProfit?.toLocaleString()}`, sub: 'per month', highlight: true },
            ].map((row, i) => (
              <div key={i} className="flex items-center justify-between p-5 rounded-xl border"
                style={{ background: row.highlight ? '#0f2a1a' : '#111', borderColor: row.highlight ? '#2d6a4f' : '#1f1f1f' }}>
                <div>
                  <div className="text-sm font-medium" style={{ color: row.highlight ? '#aaa' : '#666' }}>{row.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: '#444' }}>{row.sub}</div>
                </div>
                <div className="text-2xl font-bold" style={{ color: row.highlight ? '#4ade80' : '#f1f1f1' }}>{row.value}</div>
              </div>
            ))}
            <div className="p-5 rounded-xl border" style={{ background: '#111', borderColor: '#1f1f1f' }}>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm" style={{ color: '#666' }}>Plan cost</span>
                <span className="text-sm font-semibold" style={{ color: '#f1f1f1' }}>${plan.toLocaleString()}/mo</span>
              </div>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm" style={{ color: '#666' }}>Estimated net monthly value</span>
                <span className="text-sm font-semibold" style={{ color: results.net >= 0 ? '#4ade80' : '#f87171' }}>
                  ${results.net?.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center pt-3 border-t" style={{ borderColor: '#1f1f1f' }}>
                <span className="text-sm font-semibold" style={{ color: '#aaa' }}>ROI multiple</span>
                <span className="text-xl font-bold" style={{ color: '#4ade80' }}>{results.roiMultiple}×</span>
              </div>
            </div>
            <p className="text-xs px-1" style={{ color: '#444' }}>
              Assumes 70% AI capture rate on missed calls. Adjust close rate and profit to match your actual numbers.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;